import { apiClient, clean } from "./apiClient";
import { useSettingsStore } from "@/store/settingsStore";
import type { PageRequest, PageResponse } from "@/types/api";
import type { PropertyDetail, UserFilter, UserSummary } from "@/types/entities";

export interface CurrentUserAuthorRef {
  externalAuthorId: string | null;
  externalAuthorName: string | null;
}

function keycloakEmail(): string | null {
  const keycloak = (window as unknown as { keycloak?: { tokenParsed?: { email?: string } } }).keycloak;
  const email = keycloak?.tokenParsed?.email;
  return typeof email === "string" && email.trim() ? email.trim() : null;
}

function matchUserFromList(
  users: UserSummary[],
  userIdCandidate: string | null,
  externalUserId: string | null,
): UserSummary | undefined {
  if (userIdCandidate) {
    const byId = users.find((u) => u.id === userIdCandidate);
    if (byId) return byId;
    const byExternal = users.find((u) => u.externalId?.trim() === userIdCandidate);
    if (byExternal) return byExternal;
  }
  if (externalUserId) {
    return users.find((u) => u.externalId?.trim() === externalUserId);
  }
  return undefined;
}

function resolveExternalAuthorId(
  matched: UserSummary | undefined,
  externalUserId: string | null,
  userIdCandidate: string | null,
): string | null {
  const fromMatched = matched?.externalId?.trim();
  if (fromMatched) return fromMatched;
  if (externalUserId) return externalUserId;
  if (!userIdCandidate) return null;
  // Property `userId` is often the CRM negotiator external id (not Atlas uuid).
  if (!matched || matched.id !== userIdCandidate) return userIdCandidate;
  return null;
}

/** True when the Atlas user role is Negotiator (not Unspecified or other roles). */
export function isNegotiatorUser(user: Pick<UserSummary, "role"> | null | undefined): boolean {
  const role = user?.role?.trim().toUpperCase() ?? "";
  return role === "NEGOTIATOR";
}

/**
 * UserSummary exposes the value as `role`, while the users query sorts the
 * underlying User field named `userType`. Keep the table/URL terminology
 * user-facing and translate only at the API boundary.
 */
export function usersPageRequest(page?: PageRequest | null): PageRequest {
  if (!page || typeof page.sort !== "string") return page ?? {};

  // Support both the UI's plain field and Spring-style combined sort values,
  // while avoiding partial replacements in unrelated names such as "roles".
  const sort = page.sort.replace(/^role(?=,|$)/, "userType");
  return sort === page.sort ? page : { ...page, sort };
}

export interface KeycloakOnboardRequest {
  /** Atlas legal entity UUID — users under this LE are the onboarding scope. */
  legalEntityId: string;
  /** Branches whose users should be onboarded. NEGOTIATOR sync must already
   *  be COMPLETED for every branch in this list; the UI gates this. */
  branchIds: string[];
}

export interface KeycloakOnboardResponse {
  /** Users newly created in Keycloak by this run. */
  created: number;
  /** Users that already existed in Keycloak (matched by email) — re-linked
   *  to the correct group and given the default viewer role. */
  linked: number;
  /** Users skipped because their synced_to_keycloak flag was already true. */
  skipped: number;
  /** Users that failed to onboard. Empty on full success. */
  failed?: Array<{ email: string; reason: string }>;
}

export const usersService = {
  /**
   * The selected legal entity is sent as-is. It used to be swapped for its ROOT, which
   * turned "users of this legal entity" into "users of the whole organisation" — selecting
   * a child entity listed org-level staff and users from unrelated sibling entities. The
   * server scopes to the selected entity's own subtree.
   */
  list(filter: UserFilter, page: PageRequest): Promise<PageResponse<UserSummary>> {
    return apiClient
      .get<PageResponse<UserSummary>>("/users", {
        params: { ...clean(filter), ...clean(usersPageRequest(page)) },
      })
      .then((r) => r.data);
  },

  listNegotiators(filter: UserFilter, page: PageRequest): Promise<PageResponse<UserSummary>> {
    return usersService
      .list({ ...filter, user_type: "NEGOTIATOR" }, page)
      .then((res) => ({
        ...res,
        content: (res.content ?? []).filter(isNegotiatorUser),
      }));
  },

  /**
   * Resolve one negotiator by Atlas id or CRM external id without loading the full list.
   * Tries `GET /users/{id}` first, then `GET /users?search=…&user_type=NEGOTIATOR`.
   */
  async lookupNegotiator(
    legalEntityId: string | undefined,
    userIdCandidate: string | null | undefined,
    externalUserId: string | null | undefined = null,
  ): Promise<UserSummary | null> {
    const userId = userIdCandidate?.trim() || null;
    const externalId = externalUserId?.trim() || null;

    if (userId) {
      try {
        const one = await usersService.getOne(userId, legalEntityId);
        if (one?.id) return one;
      } catch {
        // `userId` may be a CRM external id rather than an Atlas uuid.
      }
    }

    if (externalId && externalId !== userId) {
      try {
        const one = await usersService.getOne(externalId, legalEntityId);
        if (one?.id) return one;
      } catch {
        // `externalUserId` may also be a CRM id rather than an Atlas uuid.
      }
    }

    const searchTerm = userId || externalId;
    if (!searchTerm) return null;

    const searchNegotiators = async (leId?: string) => {
      const res = await usersService.listNegotiators(
        { legalEntityId: leId, search: searchTerm },
        { page: 0, size: 10 },
      );
      return matchUserFromList(res.content ?? [], userId, externalId) ?? null;
    };

    const scoped = await searchNegotiators(legalEntityId);
    if (scoped) return scoped;
    if (legalEntityId) return searchNegotiators(undefined);
    return null;
  },

  /**
   * Resolve note author from the property's negotiator/user via `GET /users`.
   */
  async resolvePropertyUserAuthor(property: PropertyDetail): Promise<CurrentUserAuthorRef> {
    const legalEntityId =
      property.legalEntityId?.trim() || useSettingsStore.getState().legalEntityId || undefined;
    const externalUserId = property.externalUserId?.trim() || null;
    const userIdCandidate = property.userId?.trim() || null;
    const matched = await usersService.lookupNegotiator(
      legalEntityId,
      userIdCandidate,
      externalUserId,
    );

    return {
      externalAuthorId: resolveExternalAuthorId(matched ?? undefined, externalUserId, userIdCandidate),
      externalAuthorName: matched?.name?.trim() || null,
    };
  },

  /** Resolve the logged-in user's CRM `externalId` via `GET /users` (matched by email). */
  async resolveCurrentUserAuthor(email?: string | null): Promise<CurrentUserAuthorRef> {
    const resolvedEmail = email?.trim() || keycloakEmail();
    if (!resolvedEmail) {
      return { externalAuthorId: null, externalAuthorName: null };
    }

    const { legalEntityId } = useSettingsStore.getState();
    const res = await usersService.list(
      { search: resolvedEmail, legalEntityId: legalEntityId ?? undefined },
      { page: 0, size: 20 },
    );
    const normalized = resolvedEmail.toLowerCase();
    const user =
      (res.content ?? []).find((u) => u.email?.trim().toLowerCase() === normalized) ??
      (res.content ?? [])[0];

    return {
      externalAuthorId: user?.externalId?.trim() || null,
      externalAuthorName: user?.name?.trim() || null,
    };
  },
  /** Full user — `GET /api/ui/v1/users/{id}` */
  getOne: (id: string, legalEntityId?: string): Promise<UserSummary> =>
    apiClient
      .get<UserSummary>(`/users/${id}`, {
        params: legalEntityId ? { legalEntityId } : undefined,
      })
      .then((r) => r.data),
  update: (id: string, body: unknown) =>
    apiClient.patch(`/users/${id}`, body).then((r) => r.data),
  remove: (id: string) =>
    apiClient.delete(`/users/${id}`),

  /**
   * Triggers atlas-api to onboard the LE's users to Keycloak. The request
   * lands at atlas-api, which calls the Keycloak admin API on the server
   * side — the browser never talks to Keycloak directly. Atlas-api creates
   * each user in Keycloak, attaches them to the LE's group, and assigns the
   * default viewer role. Users whose {@code synced_to_keycloak} flag is
   * already true are skipped. If an email already exists in Keycloak,
   * atlas-api links that existing account to the correct group and gives it
   * the default role instead of failing.
   */
  onboardToKeycloak: (body: KeycloakOnboardRequest) =>
    apiClient
      .post<KeycloakOnboardResponse>("/users/keycloak-onboard", body)
      .then((r) => r.data),
};
